import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT } from "./content";
import Footer from "./Footer";

const SECTIONS = [
  {
    h: "Who we are",
    p: [
      `${CONTACT.company} provides locum Advanced Nurse Practitioner services to GP surgeries across the United Kingdom. This privacy policy explains how we handle information submitted through this website.`,
      `For any privacy queries, contact us at ${CONTACT.email}.`,
    ],
  },
  {
    h: "Information we collect",
    p: [
      "When you submit an enquiry through our contact form, we collect the details you provide: your name, surgery/practice name, email address, phone number (optional), the service you are interested in, and your message.",
      "We do not collect special category or patient-identifiable clinical data through this website. Please do not include patient-identifiable information in enquiry messages.",
    ],
  },
  {
    h: "How we use your information",
    p: [
      "We use the information you submit solely to respond to your enquiry, discuss potential locum cover, and provide the services requested. Enquiry details are emailed to us and stored securely so we can follow up.",
      "We do not sell your information or use it for marketing to third parties.",
    ],
  },
  {
    h: "Lawful basis",
    p: [
      "We process your information on the basis of our legitimate interest in responding to business enquiries, and to take steps at your request prior to entering into a contract for services.",
    ],
  },
  {
    h: "Data retention",
    p: [
      "We keep enquiry information only for as long as necessary to respond to you and, where relevant, to manage an ongoing or prospective working relationship. You may request deletion of your data at any time.",
    ],
  },
  {
    h: "Your rights",
    p: [
      "Under UK GDPR you have the right to access, correct, or request deletion of your personal data, to object to or restrict processing, and to data portability.",
      `To exercise any of these rights, email ${CONTACT.email}. You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk.`,
    ],
  },
  {
    h: "Third-party processors",
    p: [
      "Enquiry notifications are delivered using a third-party email provider (Resend). Your enquiry data is transmitted to us via this service purely to deliver the notification email.",
    ],
  },
  {
    h: "Contact",
    p: [
      `${CONTACT.company} — ${CONTACT.email} — ${CONTACT.phone}. ${CONTACT.area}.`,
    ],
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <header className="border-b border-surface-alt bg-bone">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="privacy-logo-link">
            <span className="font-display text-2xl tracking-tight text-forest">Wellknowe</span>
            <span className="overline text-ink-soft pb-0.5">Ltd</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono-label text-[0.78rem] uppercase tracking-[0.14em] text-ink-soft hover:text-forest transition-colors duration-200"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 md:px-10 py-20 md:py-28" data-testid="privacy-page">
        <span className="overline text-ink-soft">Legal</span>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-forest">
          Privacy Policy
        </h1>
        <p className="mt-6 text-ink-soft">
          Last updated {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
        </p>

        <div className="mt-14 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight text-forest">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="mt-4 text-ink-soft leading-relaxed">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </>
  );
}
